<template>
  <div class="cam-detail-container">
    <el-row :gutter="24">
      <el-col :md="17" :sm="24">
        <el-card class="video-main-card" shadow="never">
          <div class="video-header">
            <div class="header-left">
              <div class="cam-icon-box">
                <i class="el-icon-video-camera" />
              </div>
              <div class="cam-meta">
                <h2 class="cam-title">{{ camDetail ? camDetail.camName : '摄像头监控' }}</h2>
                <div class="status-tags">
                  <span v-if="camDetail" :class="['status-dot-tag', camDetail.onLive ? 'is-live' : 'is-playback']">
                    <span class="pulse-dot" />
                    {{ camDetail.onLive ? 'LIVE 实时画面' : '历史回放' }}
                  </span>
                  <span class="cam-id-tag">ID: {{ query.camId }}</span>
                </div>
              </div>
            </div>
            <div class="header-right">
              <el-button icon="el-icon-share" type="primary" round @click="onShareCam">授权分享</el-button>
              <el-button icon="el-icon-refresh" circle @click="() => $router.go()" />
            </div>
          </div>

          <div class="player-viewport">
            <div class="video-wrapper">
              <video
                id="videoElement"
                controls
                autoplay
                muted
                class="video-element"
              />
              <div v-if="!camDetail" class="video-loading">
                <i class="el-icon-loading" />
                <p>正在建立加密连接...</p>
              </div>
            </div>
          </div>
        </el-card>
      </el-col>

      <el-col :md="7" :sm="24">
        <el-card class="record-sidebar-card" shadow="never">
          <div slot="header" class="sidebar-header">
            <span class="sidebar-title">监控档案</span>
            <el-button type="text" icon="el-icon-date" @click="onSelectDate">选择日期</el-button>
          </div>

          <div class="current-date-bar">
            <div class="date-info">
              <span class="day">{{ calendarDate.getDate() }}</span>
              <div class="month-year">
                <span class="m">{{ calendarDate.getMonth() + 1 }}月</span>
                <span class="y">{{ calendarDate.getFullYear() }}</span>
              </div>
            </div>
            <i class="el-icon-calendar" />
          </div>

          <div class="record-list-container">
            <el-table
              v-loading="!dataList.length"
              :data="dataList"
              height="500"
              class="record-table"
              :show-header="false"
            >
              <el-table-column>
                <template slot-scope="scope">
                  <div class="record-item" @click="handlePlay(scope.row.recordId)">
                    <div class="record-icon">
                      <i class="el-icon-video-play" />
                    </div>
                    <div class="record-text">
                      <div class="record-time">{{ scope.row.startTime }}</div>
                      <div class="record-duration">持续时长 {{ scope.row.duration }}s</div>
                    </div>
                    <i class="el-icon-arrow-right arrow" />
                  </div>
                </template>
              </el-table-column>
            </el-table>
            <el-empty v-if="!dataList.length" description="当日暂无录像" :image-size="60" />
          </div>
        </el-card>
      </el-col>
    </el-row>

    <el-dialog
      title="切换监控日期"
      :visible.sync="showCalenderDialog"
      width="400px"
      append-to-body
      custom-class="pikpak-dialog"
    >
      <el-calendar v-model="calendarDate" class="mini-calendar">
        <div slot="dateCell" slot-scope="{ date, data }" class="cell-wrapper" @click="handleCellClick(date, data)">
          <span :class="{ 'has-data': dealMyDate(data.day) }">{{ data.day.split('-').slice(2).join() }}</span>
          <div v-if="dealMyDate(data.day)" class="data-dot" />
        </div>
      </el-calendar>
    </el-dialog>

    <el-dialog
      title="授权给好友"
      :visible.sync="showCreateShareDialog"
      width="380px"
      append-to-body
      custom-class="pikpak-dialog"
    >
      <div class="share-box">
        <p class="share-tip">请选择有权查看此摄像头画面的好友</p>
        <div class="user-select-grid">
          <el-checkbox-group v-model="createShareForm.shareToList">
            <el-checkbox v-for="user in userContactList" :key="user.userIdStr" :label="user.username" border>
              <div class="user-cell">
                <el-avatar :size="24" icon="el-icon-user" />
                <span>{{ user.username }}</span>
              </div>
            </el-checkbox>
          </el-checkbox-group>
        </div>
      </div>
      <div slot="footer">
        <el-button type="primary" round block style="width: 100%" @click="createShare">确认授权</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import flvjs from 'flv.js'

import { createShare, getCamDetail, getRecordByMonth, getRecordUrl } from '@/api/disk'
import { getUserContact } from '@/api/user'

export default {
  name: 'CamDetail',
  data() {
    return {
      calendarDate: new Date(),
      dateMap: new Map(),
      query: {
        camId: '',
        yearMonth: '',
        yearMonthDay: ''
      },
      dataList: [],
      showCalenderDialog: false,
      camDetail: null,
      camRecordDetail: null,
      // ****************************************************************************************************************
      showCreateShareDialog: false,
      userContactList: [],
      createShareForm: {
        albumType: 1,
        albumId: null,
        shareToList: []
      },
      flvPlayer: null
    }
  },
  watch: {
    $route() {
      this.$router.go()
    },
    calendarDate: {
      handler(newValue, oldValue) {
        const oldMonth = this.getYearMonth(oldValue)
        const newMonth = this.getYearMonth(newValue)
        if (oldMonth !== newMonth) {
          this.query.yearMonth = this.getYearMonth(newValue)
          getRecordByMonth(this.query).then(resp => {
            if (resp.code === 0) {
              this.dateMap.clear()
              for (const item of resp.data) {
                const date1 = new Date(item)
                const dayStr = this.getYearMonthDay(date1)
                this.dateMap.set(dayStr, date1)
              }
              // 对 calendarDate 赋值重新渲染日历, 目的是调用 dealMyDate 处理当月的日期
              this.calendarDate = new Date(newMonth)
            }
          }).catch(error => {
            this.$message.error(error.message)
          })
        }
      }
    }
  },
  created() {
    document.title = '摄像头监控'
    this.query.camId = this.$route.query.camId
    if (this.query.camId) {
      this.getData()
    } else {
      this.$message.error('参数错误')
      this.goBack()
    }
  },
  methods: {
    getData() {
      getCamDetail(this.query).then(resp => {
        if (resp.code === 0) {
          this.camDetail = resp.data
          if (this.camDetail.onLive) {
            this.initVideoPlayer(this.camDetail.liveUrl, true)
          } else {
            this.initVideoPlayer(this.camDetail.url, false)
          }
        }
      })
    },
    goBack() {
      this.$router.go(-1)
    },
    handleCellClick(date, data) {
      this.showCalenderDialog = false
      this.query.yearMonthDay = this.getYearMonthDay(date)
      getCamDetail(this.query).then(resp => {
        if (resp.code === 0) {
          const respData = resp.data
          this.camDetail = respData
          this.dataList = respData.dayRecords
        }
      })
    },
    // 渲染日历时会处理每个日期
    dealMyDate(val) {
      return this.dateMap.get(val) !== undefined
    },
    getYearMonth(date) {
      const year = date.getFullYear().toString().padStart(4, '0')
      const month = (date.getMonth() + 1).toString().padStart(2, '0')
      // const day = date.getDate().toString().padStart(2, '0')
      // const hour = date.getHours().toString().padStart(2, '0')
      // const minute = date.getMinutes().toString().padStart(2, '0')
      // const second = date.getSeconds().toString().padStart(2, '0')
      // 2023-02-16 08:25:05
      // console.log(`${year}-${month}-${day} ${hour}:${minute}:${second}`)
      return year + '-' + month
    },
    getYearMonthDay(date) {
      const year = date.getFullYear().toString().padStart(4, '0')
      const month = (date.getMonth() + 1).toString().padStart(2, '0')
      const day = date.getDate().toString().padStart(2, '0')
      return year + '-' + month + '-' + day
    },
    handlePlay(recordId) {
      getRecordUrl(recordId).then(resp => {
        if (resp.code === 0) {
          this.camRecordDetail = resp.data
          this.initVideoPlayer(this.camRecordDetail.url, false)
        } else {
          this.$message.error(resp.msg)
        }
      })
    },
    initVideoPlayer(videoUrl, live) {
      var videoElement = document.getElementById('videoElement')
      if (!live) {
        videoElement.src = videoUrl
        return
      }

      if (!flvjs.isSupported()) {
        this.$message.error('not support flv')
        return
      }

      this.flvPlayer = flvjs.createPlayer({
        type: 'flv',
        isLive: true,
        url: videoUrl,
        duration: 0,
        filesize: 0,
        enableStashBuffer: false,
        hasAudio: true,
        hasVideo: true
      })
      this.flvPlayer.attachMediaElement(videoElement)
      this.flvPlayer.load()
      this.flvPlayer.play()

      videoElement.addEventListener('progress', () => {
        const end = this.flvPlayer.buffered.end(0) // 获取当前buffered值(缓冲区末尾)
        const delta = end - this.flvPlayer.currentTime // 获取buffered与当前播放位置的差值
        // 延迟过大，通过跳帧的方式更新视频
        if (delta > 10 || delta < 0) {
          this.flvPlayer.currentTime = this.flvPlayer.buffered.end(0) - 1
          return
        }

        // 追帧
        if (delta > 1) {
          videoElement.playbackRate = 1.1
        } else {
          videoElement.playbackRate = 1
        }
      })
      // 点击播放按钮后，更新视频
      videoElement.addEventListener('play', () => {
        if (this.flvPlayer.buffered.length > 0) {
          const end = this.flvPlayer.buffered.end(0) - 1
          this.flvPlayer.currentTime = end
        }
      })
      // 网页重新激活后，更新视频
      window.onfocus = () => {
        const end = this.flvPlayer.buffered.end(0) - 1
        this.flvPlayer.currentTime = end
      }
    },
    onSelectDate() {
      this.showCalenderDialog = true
    },
    onShareCam() {
      this.createShareForm.albumId = this.camDetail.camId
      getUserContact(1).then(resp => {
        if (resp.code === 0) {
          this.userContactList = resp.data
          this.showCreateShareDialog = true
        } else {
          this.$message.error(resp.msg)
        }
      })
    },
    createShare() {
      createShare(this.createShareForm).then(resp => {
        this.$message.info(resp.msg)
      }).finally(() => {
        this.showCreateShareDialog = false
        this.createShareForm = {
          albumType: 1,
          albumId: null,
          shareToList: []
        }
      })
    }
  }
}
</script>

<style scoped>
.cam-detail-container {
  padding: 24px;
}

/* 播放器卡片 */
.video-main-card {
  border-radius: 20px;
  border: none;
  background: #fff;
}

.video-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 0 20px;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 16px;
}

.cam-icon-box {
  width: 48px;
  height: 48px;
  background: #e8f0fe;
  border-radius: 12px;
  display: flex;
  justify-content: center;
  align-items: center;
  color: #1a73e8;
  font-size: 24px;
}

.cam-title {
  font-size: 18px;
  font-weight: 700;
  margin: 0 0 6px 0;
  color: #1a1a1a;
}

.status-tags {
  display: flex;
  gap: 8px;
  align-items: center;
}

.status-dot-tag {
  display: flex;
  align-items: center;
  padding: 2px 10px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
}

.is-live {
  background: #fef0f0;
  color: #f56c6c;
}

.is-playback {
  background: #f4f4f5;
  color: #909399;
}

.pulse-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-right: 6px;
  background: currentColor;
}

.is-live .pulse-dot {
  animation: pulse 1.5s infinite;
}

@keyframes pulse {
  0% { transform: scale(1); opacity: 1; }
  50% { transform: scale(1.5); opacity: 0.5; }
  100% { transform: scale(1); opacity: 1; }
}

.cam-id-tag {
  font-size: 11px;
  color: #c0c4cc;
}

.player-viewport {
  background: #000;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: inset 0 0 40px rgba(0,0,0,0.5);
}

.video-wrapper {
  position: relative;
  width: 100%;
  padding-top: 56.25%;
}

.video-element {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}

/* 侧边栏档案 */
.record-sidebar-card {
  border-radius: 20px;
  border: none;
  height: 100%;
}

.sidebar-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.sidebar-title {
  font-weight: 700;
  font-size: 16px;
}

.current-date-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  background: #f8f9fa;
  border-radius: 12px;
  margin-bottom: 20px;
}

.date-info {
  display: flex;
  align-items: center;
  gap: 12px;
}

.date-info .day {
  font-size: 32px;
  font-weight: 800;
  color: #1a73e8;
}

.month-year {
  display: flex;
  flex-direction: column;
  line-height: 1.2;
}

.month-year .m { font-weight: 700; font-size: 14px; }
.month-year .y { font-size: 11px; color: #909399; }

.current-date-bar i {
  font-size: 24px;
  color: #dcdfe6;
}

.record-list-container {
  margin: 0 -10px;
}

.record-item {
  display: flex;
  align-items: center;
  padding: 12px;
  cursor: pointer;
  border-radius: 10px;
  transition: all 0.2s;
}

.record-item:hover {
  background: #f0f7ff;
}

.record-icon {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: #fff;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-right: 12px;
  box-shadow: 0 2px 6px rgba(0,0,0,0.05);
}

.record-icon i { color: #1a73e8; font-size: 18px; }

.record-text { flex: 1; }
.record-time { font-weight: 600; font-size: 14px; color: #303133; }
.record-duration { font-size: 11px; color: #909399; margin-top: 2px; }

.arrow { color: #dcdfe6; font-size: 14px; }

/* 日历弹窗美化 */
::v-deep .pikpak-dialog {
  border-radius: 20px;
}

::v-deep .mini-calendar .el-calendar-table .el-calendar-day {
  height: 50px;
  padding: 4px;
}

.cell-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
}

.has-data {
  color: #1a73e8;
  font-weight: 800;
}

.data-dot {
  width: 4px;
  height: 4px;
  background: #1a73e8;
  border-radius: 50%;
  margin-top: 4px;
}

/* 分享列表 */
.share-tip { font-size: 13px; color: #909399; margin-bottom: 16px; }
.user-select-grid { max-height: 300px; overflow-y: auto; }

::v-deep .user-select-grid .el-checkbox {
  width: 100%;
  margin: 0 0 10px 0 !important;
  border-radius: 10px;
  padding: 10px;
}

.user-cell { display: flex; align-items: center; gap: 10px; }
</style>
